import type { QrCaptureType } from "types";

// Numeric extensions retain their existing stored confirmation representation.
// Parameterized extensions include their colon-separated QR configuration.
export type OrbExtension = number | string;

export interface OrbDetailValue {
  label_id: string;
  value: string;
}

export const ORB_RETRY_COUNT_LABEL_ID = "orb_retry_count";

export function getOrbStepLabelDetails(stepLabel: string): OrbDetailValue[] {
  const pairs = stepLabel
    .replace(/\n/g, " ")
    .split(" ")
    .filter((part) => part.includes(":"));
  if (pairs.length === 0) return [{ label_id: "step_label", value: stepLabel }];
  return pairs.map((pair) => {
    const separator = pair.indexOf(":");
    return {
      label_id: pair.slice(0, separator),
      value: pair.slice(separator + 1),
    };
  });
}

export function getOrbRetryDetail(retryCount: number): OrbDetailValue[] {
  return retryCount > 0
    ? [
        {
          label_id: ORB_RETRY_COUNT_LABEL_ID,
          value: String(retryCount),
        },
      ]
    : [];
}

// RTMS shares the Orb QR envelope while retaining its own retry label.
export function getQrRetryDetail(
  retryCount: number,
  captureType: QrCaptureType
): OrbDetailValue[] {
  if (captureType === "orb") return getOrbRetryDetail(retryCount);
  return retryCount > 0
    ? [{ label_id: "rtms_retry_count", value: String(retryCount) }]
    : [];
}

export function getApplicableStoredOrbDetails(
  storedDetails: any,
  configuredLabelIds: string[]
) {
  if (!storedDetails) return storedDetails;

  const allowedLabels = new Set(configuredLabelIds);
  const labels = Object.fromEntries(
    Object.entries(storedDetails.labels || {}).filter(([key]) =>
      allowedLabels.has(key)
    )
  );

  return { ...storedDetails, labels };
}

export function getApplicableOrbDetailValues<T extends OrbDetailValue>(
  values: T[] | undefined,
  configuredLabelIds: string[]
): T[] {
  const allowedLabels = new Set(configuredLabelIds);
  return (values || []).filter((field) => allowedLabels.has(field.label_id));
}

export function getOrbDetailPairs(
  storedDetails: any,
  stepDetails: OrbDetailValue[] = []
): string[] {
  const detailPairs: string[] = [];

  if (storedDetails?.device) {
    detailPairs.push(`device,${storedDetails.device}`);
  }
  if (storedDetails?.camera_not_flipped) {
    detailPairs.push(`camera_not_flipped,${storedDetails.camera_not_flipped}`);
  }
  if (storedDetails?.labels && typeof storedDetails.labels === "object") {
    Object.entries(storedDetails.labels).forEach(([key, value]) => {
      if (!value) return;
      const valueString = Array.isArray(value)
        ? value.join(",")
        : String(value);
      detailPairs.push(`${key},${valueString}`);
    });
  }
  if (storedDetails?.data && Array.isArray(storedDetails.data)) {
    storedDetails.data
      .filter((item: string) => {
        const trimmedItem = item.trim();
        return trimmedItem && !trimmedItem.includes(": ");
      })
      .forEach((item: string, index: number) => {
        detailPairs.push(`data${index + 1},${item.trim()}`);
      });
  }

  stepDetails.forEach(({ label_id, value }) => {
    const key = label_id?.trim();
    const detailValue = value?.trim();
    if (key && detailValue) {
      detailPairs.push(`${key},${detailValue}`);
    }
  });

  return detailPairs;
}

export function buildOrbQrContent({
  userId,
  sessionId,
  captureTaskId,
  contributor,
  detailPairs,
  extension,
}: {
  userId: string;
  sessionId: string;
  captureTaskId?: string;
  contributor?: string;
  detailPairs?: string[];
  extension: OrbExtension;
}): string {
  const userIdForDisplay = userId.split(":")[1] || userId;
  let content = `userid:${userIdForDisplay}:1:ss${sessionId}`;

  if (captureTaskId) {
    content += `:tk${captureTaskId}`;
  }
  if (contributor) {
    content += `:cl${contributor}`;
  }
  if (detailPairs?.length) {
    content += `:details${detailPairs.join("|")}`;
  }

  return `${content}::${extension}::`;
}

export function createOrbConfirmationFingerprint({
  captureTaskId,
  extension,
  storedDetails,
  stepDetails,
  stepLabel,
}: {
  captureTaskId: string;
  extension: OrbExtension;
  storedDetails: any;
  stepDetails?: OrbDetailValue[];
  stepLabel?: string;
}): string {
  return JSON.stringify({
    version: 1,
    captureTaskId,
    extension,
    detailPairs: getOrbDetailPairs(storedDetails, [
      ...(stepDetails || []),
      ...(stepLabel ? getOrbStepLabelDetails(stepLabel) : []),
    ]),
  });
}
